// Supabase fetch-plugin adapter — owned by the workspace layer.
//
// The framework (smbls@799bf2224) removed its builtin supabase adapter from
// `@symbo.ls/fetch` (the PLATFORM has no Supabase), but customer projects
// configured with `db: { adapter: 'supabase' }` — their own bring-your-own
// Supabase backends — remain a product feature of the editor. The adapter
// therefore lives HERE, next to the `@supabase/supabase-js` dependency
// (packages/shared/package.json), and is registered into the fetch plugin's
// cross-bundle registry via `registerAdapter('supabase', …)` in
// attachDbAdapters.js. Public surface is identical to the old builtin:
// `setup(options) -> { name, client, select, rpc, insert, update, upsert,
// delete, subscribe, getSession, getUser, signIn, signUp, signOut,
// onAuthStateChange, upload, download, getPublicUrl }`.
//
// FA204 / frank trap — clientCacheKey/applyFilters/applyModifiers used to
// live at module scope, plus bare sibling-export calls (setup ->
// createSupabaseClient/supabaseAdapter) between the exports below. Each now
// carries its own self-contained inline copy of whichever locals/siblings
// it actually calls. CLIENT_CACHE is genuine cross-call state (its whole
// point is to survive across separate `setup` calls against the same
// project, so a fresh per-call Map would defeat it and reintroduce the
// "Multiple GoTrueClient instances" warning this cache exists to prevent) —
// re-homed onto `globalThis.__supabaseFetchAdapterClientCache`, the same
// argument-less escape hatch as pointerCapability.js's
// `window.__pointerCapabilityState`.

import { createClient as supabaseCreateClient } from '@supabase/supabase-js'

// Browser-WebSocket-aware wrapper around supabase's createClient.
// See ./browserWebSocket.js for the detection-bypass rationale —
// passing `realtime: { transport: <the browser WebSocket> }` skips
// supabase's environment auto-detection (which mis-fires under
// Parcel's `process` polyfill). attachDbAdapters.js imports this as
// its `supabase` ADAPTER_FACTORIES entry — single wrapper, no copies.
export const createSupabaseClient = (url, key, options = {}) => {
  // The browser WebSocket, read at call time (see ./browserWebSocket.js —
  // inlined so this serialized function stays self-contained).
  const webSocket =
    (typeof globalThis !== 'undefined' && globalThis.WebSocket) ||
    (typeof window !== 'undefined' && window.WebSocket) ||
    null
  if (webSocket) {
    options = {
      ...options,
      realtime: {
        transport: webSocket,
        ...(options.realtime || {})
      }
    }
  }
  return supabaseCreateClient(url, key, options)
}

export const setup = async ({
  url,
  key,
  projectId,
  createClient,
  auth,
  retry,
  adapter,
  ...supabaseOptions
}) => {
  // FA204 / frank trap — self-contained inline copies, see the file header.
  const _createSupabaseClient = (url, key, options = {}) => {
    const webSocket =
      (typeof globalThis !== 'undefined' && globalThis.WebSocket) ||
      (typeof window !== 'undefined' && window.WebSocket) ||
      null
    if (webSocket) {
      options = {
        ...options,
        realtime: {
          transport: webSocket,
          ...(options.realtime || {})
        }
      }
    }
    return supabaseCreateClient(url, key, options)
  }
  // Cache Supabase clients by (url, key, authMode) so every `setup` call
  // against the same project reuses a single GoTrueClient. Without this
  // cache, each fresh adapter resolution (per db/fetch config, per query)
  // spins up a new GoTrueClient on the same storage key — Supabase logs
  // "Multiple GoTrueClient instances" warnings and auth state can drift.
  const CLIENT_CACHE = (globalThis.__supabaseFetchAdapterClientCache =
    globalThis.__supabaseFetchAdapterClientCache || new Map())
  const _clientCacheKey = (url, key, authOff) => `${url}|${key}|${authOff ? 'off' : 'on'}`
  const _applyFilters = (query, params) => {
    if (!params) return query
    for (const key in params) {
      const val = params[key]
      if (val === null) {
        query = query.is(key, null)
      } else if (Array.isArray(val)) {
        query = query.in(key, val)
      } else if (typeof val === 'object') {
        for (const op in val) {
          if (op === 'gt') query = query.gt(key, val[op])
          else if (op === 'gte') query = query.gte(key, val[op])
          else if (op === 'lt') query = query.lt(key, val[op])
          else if (op === 'lte') query = query.lte(key, val[op])
          else if (op === 'neq') query = query.neq(key, val[op])
          else if (op === 'like') query = query.like(key, val[op])
          else if (op === 'ilike') query = query.ilike(key, val[op])
          else if (op === 'in') query = query.in(key, val[op])
          else if (op === 'is') query = query.is(key, val[op])
          else if (op === 'contains') query = query.contains(key, val[op])
          else if (op === 'containedBy') query = query.containedBy(key, val[op])
          else if (op === 'textSearch') query = query.textSearch(key, val[op])
        }
      } else {
        query = query.eq(key, val)
      }
    }
    return query
  }
  const _applyModifiers = (query, { limit, offset, order, single } = {}) => {
    if (order) {
      if (Array.isArray(order)) {
        for (const o of order) {
          const by = typeof o === 'string' ? o : o.by
          query = query.order(by, { ascending: o.asc !== false })
        }
      } else {
        const orderBy = typeof order === 'string' ? order : order.by
        query = query.order(orderBy, { ascending: order.asc !== false })
      }
    }
    if (limit) query = query.limit(limit)
    if (offset) query = query.range(offset, offset + (limit || 20) - 1)
    if (single) query = query.single()
    return query
  }
  const _supabaseAdapter = (client) => ({
    name: 'supabase',
    client,
    getSession: async () => {
      const { data } = await client.auth.getSession()
      return data?.session || null
    },
    getUser: async () => {
      const { data } = await client.auth.getUser()
      return data?.user || null
    },
    signIn: (credentials) => {
      if (credentials.provider) {
        return client.auth.signInWithOAuth({ provider: credentials.provider })
      }
      if (credentials.token) {
        return client.auth.signInWithIdToken(credentials)
      }
      return client.auth.signInWithPassword(credentials)
    },
    signUp: (credentials) => client.auth.signUp(credentials),
    signOut: () => client.auth.signOut(),
    onAuthStateChange: (callback) => {
      const {
        data: { subscription }
      } = client.auth.onAuthStateChange((event, session) => callback(event, session))
      return () => subscription.unsubscribe()
    },
    select: async ({ from, schema, select, params, headers, ...modifiers }) => {
      const base = schema ? client.schema(schema) : client
      let q = base.from(from).select(select || '*')
      q = _applyFilters(q, params)
      q = _applyModifiers(q, modifiers)
      if (headers) {
        try {
          for (const k in headers) {
            if (q.setHeader) q = q.setHeader(k, headers[k])
            else if (client.rest) client.rest.headers[k] = headers[k]
          }
        } catch (e) {
          console.warn('[supabase] setHeader failed:', e.message)
        }
      }
      return q
    },
    rpc: ({ from, params }) => client.rpc(from, params),
    insert: ({ from, schema, data, select }) => {
      const base = schema ? client.schema(schema) : client
      return base
        .from(from)
        .insert(data)
        .select(select || '*')
    },
    update: ({ from, schema, data, params, select }) => {
      const base = schema ? client.schema(schema) : client
      let q = base.from(from).update(data)
      q = _applyFilters(q, params)
      return q.select(select || '*')
    },
    upsert: ({ from, schema, data, select }) => {
      const base = schema ? client.schema(schema) : client
      return base
        .from(from)
        .upsert(data)
        .select(select || '*')
    },
    delete: ({ from, params }) => {
      let q = client.from(from).delete()
      q = _applyFilters(q, params)
      return q
    },
    subscribe: ({ from, params, on }, callback) => {
      const event = on || '*'
      const channel = client
        .channel(`db-${from}-${Date.now()}`)
        .on(
          'postgres_changes',
          {
            event,
            schema: 'public',
            table: from,
            ...(params?.id ? { filter: `id=eq.${params.id}` } : {})
          },
          (payload) => callback(payload.new, payload.old, payload)
        )
        .subscribe()
      return () => client.removeChannel(channel)
    },
    upload: ({ bucket, path, file, options }) =>
      client.storage.from(bucket).upload(path, file, options),
    download: ({ bucket, path }) => client.storage.from(bucket).download(path),
    getPublicUrl: ({ bucket, path }) => client.storage.from(bucket).getPublicUrl(path)
  })

  const supabaseUrl = url || (projectId && `https://${projectId}.supabase.co`)
  if (!supabaseUrl || !key) {
    throw new Error('supabase adapter: url (or projectId) and key are required')
  }
  // attachDbAdapters wires `createClient` onto the config before transport
  // strips it; when a config arrives bare (direct resolveDb call, unit
  // tests) fall back to the wrapper above — same factory either way.
  if (!createClient) createClient = _createSupabaseClient
  // Disable Supabase auto-auth when auth: false (prevents navigator.locks hang)
  if (auth === false) {
    supabaseOptions.auth = {
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false
    }
  }
  const cacheKey = _clientCacheKey(supabaseUrl, key, auth === false)
  let client = CLIENT_CACHE.get(cacheKey)
  if (!client) {
    // `createClient` may be sync (default supabase-js) OR async — the SDK's
    // supabasePassthrough returns a promise so it can lazy-resolve the
    // optional peer dep. Awaiting works for both.
    client = await createClient(supabaseUrl, key, supabaseOptions)
    if (client?.auth) CLIENT_CACHE.set(cacheKey, client)
  }
  if (!client || !client.auth) return null
  return _supabaseAdapter(client)
}

export const supabaseAdapter = (client) => {
  // FA204 / frank trap — self-contained inline copies of applyFilters/
  // applyModifiers, see the file header.
  const _applyFilters = (query, params) => {
    if (!params) return query
    for (const key in params) {
      const val = params[key]
      if (val === null) {
        query = query.is(key, null)
      } else if (Array.isArray(val)) {
        query = query.in(key, val)
      } else if (typeof val === 'object') {
        for (const op in val) {
          if (op === 'gt') query = query.gt(key, val[op])
          else if (op === 'gte') query = query.gte(key, val[op])
          else if (op === 'lt') query = query.lt(key, val[op])
          else if (op === 'lte') query = query.lte(key, val[op])
          else if (op === 'neq') query = query.neq(key, val[op])
          else if (op === 'like') query = query.like(key, val[op])
          else if (op === 'ilike') query = query.ilike(key, val[op])
          else if (op === 'in') query = query.in(key, val[op])
          else if (op === 'is') query = query.is(key, val[op])
          else if (op === 'contains') query = query.contains(key, val[op])
          else if (op === 'containedBy') query = query.containedBy(key, val[op])
          else if (op === 'textSearch') query = query.textSearch(key, val[op])
        }
      } else {
        query = query.eq(key, val)
      }
    }
    return query
  }
  const _applyModifiers = (query, { limit, offset, order, single } = {}) => {
    if (order) {
      if (Array.isArray(order)) {
        for (const o of order) {
          const by = typeof o === 'string' ? o : o.by
          query = query.order(by, { ascending: o.asc !== false })
        }
      } else {
        const orderBy = typeof order === 'string' ? order : order.by
        query = query.order(orderBy, { ascending: order.asc !== false })
      }
    }
    if (limit) query = query.limit(limit)
    if (offset) query = query.range(offset, offset + (limit || 20) - 1)
    if (single) query = query.single()
    return query
  }

  return {
    name: 'supabase',
    client,

    // Auth
    getSession: async () => {
      const { data } = await client.auth.getSession()
      return data?.session || null
    },

    getUser: async () => {
      const { data } = await client.auth.getUser()
      return data?.user || null
    },

    signIn: (credentials) => {
      if (credentials.provider) {
        return client.auth.signInWithOAuth({ provider: credentials.provider })
      }
      if (credentials.token) {
        return client.auth.signInWithIdToken(credentials)
      }
      return client.auth.signInWithPassword(credentials)
    },

    signUp: (credentials) => client.auth.signUp(credentials),

    signOut: () => client.auth.signOut(),

    onAuthStateChange: (callback) => {
      const {
        data: { subscription }
      } = client.auth.onAuthStateChange((event, session) => callback(event, session))
      return () => subscription.unsubscribe()
    },

    // CRUD
    select: async ({ from, schema, select, params, headers, ...modifiers }) => {
      const base = schema ? client.schema(schema) : client
      let q = base.from(from).select(select || '*')
      q = _applyFilters(q, params)
      q = _applyModifiers(q, modifiers)
      if (headers) {
        try {
          for (const k in headers) {
            if (q.setHeader) q = q.setHeader(k, headers[k])
            else if (client.rest) client.rest.headers[k] = headers[k]
          }
        } catch (e) {
          console.warn('[supabase] setHeader failed:', e.message)
        }
      }
      return q
    },

    rpc: ({ from, params }) => client.rpc(from, params),

    insert: ({ from, schema, data, select }) => {
      const base = schema ? client.schema(schema) : client
      return base
        .from(from)
        .insert(data)
        .select(select || '*')
    },

    update: ({ from, schema, data, params, select }) => {
      const base = schema ? client.schema(schema) : client
      let q = base.from(from).update(data)
      q = _applyFilters(q, params)
      return q.select(select || '*')
    },

    upsert: ({ from, schema, data, select }) => {
      const base = schema ? client.schema(schema) : client
      return base
        .from(from)
        .upsert(data)
        .select(select || '*')
    },

    delete: ({ from, params }) => {
      let q = client.from(from).delete()
      q = _applyFilters(q, params)
      return q
    },

    // Realtime
    subscribe: ({ from, params, on }, callback) => {
      const event = on || '*'
      const channel = client
        .channel(`db-${from}-${Date.now()}`)
        .on(
          'postgres_changes',
          {
            event,
            schema: 'public',
            table: from,
            ...(params?.id ? { filter: `id=eq.${params.id}` } : {})
          },
          (payload) => callback(payload.new, payload.old, payload)
        )
        .subscribe()

      return () => client.removeChannel(channel)
    },

    // Storage
    upload: ({ bucket, path, file, options }) =>
      client.storage.from(bucket).upload(path, file, options),

    download: ({ bucket, path }) => client.storage.from(bucket).download(path),

    getPublicUrl: ({ bucket, path }) => client.storage.from(bucket).getPublicUrl(path)
  }
}
